import { AlertTriangle, KeyRound, RefreshCw, Save, ShieldCheck, X } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { api } from "./api";
import "./security-controller.css";

export function SecurityController() {
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    function openFromSettings(event: MouseEvent) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest("button");
      if (!button || !button.closest(".settings-grid") || button.textContent?.trim() !== "Change password") return;
      event.preventDefault();
      setError("");
      setNotice("");
      setOpen(true);
    }
    document.addEventListener("click", openFromSettings);
    return () => document.removeEventListener("click", openFromSettings);
  }, []);

  useEffect(() => {
    if (!open) return;
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape" && !busy) setOpen(false);
    }
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [busy, open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setNotice("");
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }
    if (currentPassword === newPassword) {
      setError("Choose a new password that is different from the current password.");
      return;
    }
    setBusy(true);
    try {
      await api<{ passwordChanged: boolean; sessionsRevoked: boolean }>("/api/auth/change-password", {
        method: "POST",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setNotice("Password changed. Other signed-in sessions were revoked.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not change the password.");
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return <div className="security-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) setOpen(false); }}>
    <section className="security-dialog" role="dialog" aria-modal="true" aria-labelledby="security-dialog-title">
      <div className="security-dialog-heading">
        <span><KeyRound /></span>
        <div><p className="eyebrow">Owner security</p><h2 id="security-dialog-title">Change password</h2><p>Confirm the current password, then choose a new password with at least 12 characters.</p></div>
        <button className="icon-button" type="button" aria-label="Close password dialog" disabled={busy} onClick={() => setOpen(false)}><X size={18} /></button>
      </div>

      {error && <div className="inline-error"><AlertTriangle size={16} /> {error}</div>}
      {notice && <div className="inline-success"><ShieldCheck size={16} /> {notice}</div>}

      <form onSubmit={(event) => void submit(event)}>
        <label>Current password<input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} autoComplete="current-password" maxLength={128} required autoFocus /></label>
        <label>New password<input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} autoComplete="new-password" minLength={12} maxLength={128} required /></label>
        <label>Confirm new password<input type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} autoComplete="new-password" minLength={12} maxLength={128} required /></label>
        <div className="security-dialog-note"><ShieldCheck size={16} /><p>Changing the password revokes every older dashboard session. This browser receives a fresh session automatically.</p></div>
        <div className="security-dialog-actions"><button className="secondary-button" type="button" disabled={busy} onClick={() => setOpen(false)}>Cancel</button><button className="primary-button" type="submit" disabled={busy}>{busy ? <RefreshCw className="spin" size={16} /> : <Save size={16} />} {busy ? "Changing…" : "Change password"}</button></div>
      </form>
    </section>
  </div>;
}
